import { API_BASE, getDesktopBridge, type NeosocialDesktopBridge } from '@neosocial/protocol';
import { storage } from './storage';

/**
 * The desktop bridge, when the Tauri shell provides one
 * (docs/build/desktop-bridge.md; types in @neosocial/protocol).
 */
export function desktopBridge(): NeosocialDesktopBridge | undefined {
  return getDesktopBridge() ?? undefined;
}

/**
 * The phone app (apps/mobile): this client built as the "mobile" flavor
 * (`build:mobile`, .env.mobile), bundled in the iOS and Android app. Read at
 * build time, so mobile-only code and desktop-only paths compile in or out.
 */
export function isMobileApp(): boolean {
  return import.meta.env.VITE_FLAVOR === 'mobile';
}

/**
 * The packaged desktop app: it loads the client from its own
 * `http://tauri.localhost`, which is never the server, so it needs an
 * address (the dev build loads from the Vite server, which proxies /api).
 */
export function isPackagedApp(): boolean {
  return (
    !!desktopBridge() && typeof location !== 'undefined' && location.hostname === 'tauri.localhost'
  );
}

/**
 * Where the server lives. On the web this is the page's own origin (Caddy
 * proxies /api, /gateway and /voice). A packaged desktop build has no usable
 * origin, so the bridge (or a saved override) supplies one.
 */
export function serverOrigin(): string {
  const fromBridge = desktopBridge()?.serverOrigin;
  if (fromBridge) return fromBridge.replace(/\/+$/, '');
  const saved = storage.get('server-origin');
  if (saved) return saved.replace(/\/+$/, '');
  const env = (import.meta.env?.VITE_SERVER_ORIGIN as string | undefined) ?? '';
  if (env) return env.replace(/\/+$/, '');
  if (typeof location !== 'undefined' && /^https?:$/.test(location.protocol))
    return location.origin;
  return '';
}

/**
 * The server's host as people know it ("chat.example.ir"), for display.
 * Not `location.host`: in the packaged desktop app that is "tauri.localhost".
 */
export function serverHost(): string {
  const origin = serverOrigin() || (typeof location !== 'undefined' ? location.origin : '');
  try {
    return origin ? new URL(origin).host : '';
  } catch {
    return '';
  }
}

export function apiUrl(path: string): string {
  return `${serverOrigin()}${API_BASE}${path}`;
}

/** A WebSocket URL on the server origin (`/gateway`, `/voice/ws`). */
export function wsUrl(path: string): string {
  const origin =
    serverOrigin() || (typeof location !== 'undefined' ? location.origin : 'http://localhost');
  return origin.replace(/^http/, 'ws') + path;
}

/** Makes an API-relative asset path (avatars, attachments) absolute. */
export function assetUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^(https?:|data:|blob:)/.test(path)) return path;
  return `${serverOrigin()}${path}`;
}

/**
 * The desktop shell's rule for a server origin (apps/desktop bridge.rs
 * normalize_origin): https with a host, or plain http only for loopback; no
 * path, query, fragment or credentials. Returns the origin, or null.
 */
export function normalizeServerOrigin(raw: string): string | null {
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  const loopback = ['127.0.0.1', 'localhost', '[::1]'].includes(u.hostname);
  if (u.protocol !== 'https:' && !(u.protocol === 'http:' && loopback)) return null;
  if (!u.hostname || u.username || u.password) return null;
  if ((u.pathname !== '/' && u.pathname !== '') || u.search || u.hash) return null;
  return u.origin;
}
